import { useParams } from "react-router-dom";
import { useCallback, useEffect, useState } from "react";
import api from "api";
import { ProductType, RootState } from "types";
import { useSelector } from "react-redux";
import { useQuery } from "./query";

interface OnClickProps {
    type: "expand" | "navigate" | "callback" | "none";
    params?: any;
}

interface SidenavItem {
    name: string;
    description: string;
    price: number;
    children: SidenavItem[];
    onClick: OnClickProps;
    disabled: boolean;
    projectTypeIds: number[];
    icon?: string;
}

export const useSidenavContent = () => {
    const params: any = useParams();
    const project = useSelector((state: RootState) => state.project);
    const [products, setProducts] = useState<ProductType[]>([]);
    const [state, setState] = useState<SidenavItem[]>([]);
    const query = useQuery();

    const getProducts = useCallback(async () => {
        const data = await api.clients.getAllProducts(params.clientId);
        setProducts(data);
    }, [params.clientId]);

    const checkDisabled = useCallback(
        (obj: any[]) => {
            if (products.length) {
                const p = products.map((x) => x.ProductCode);
                const projectTypeId = project?.ProjectTypeId;
                const element = obj.find((x: any) => x.projectTypeId === projectTypeId);
                if (element) {
                    const productCodes = element.productCodes;

                    if (p.some((x) => productCodes.includes(x))) {
                        return false;
                    }
                }
                return true;
            }
            return true;
        },
        [products, project]
    );

    const setStream = useCallback(
        (id: string) => {
            query.set("streams", id);
            return query;
        },
        [query]
    );

    const getStreamIds = () => {
        const search = new URLSearchParams(window.location.search);
        const streamIds = search.get("streams");
        return streamIds;
    };

    useEffect(() => {
        getProducts();
    }, [getProducts]);

    useEffect(() => {
        const data: SidenavItem[] = [
            {
                name: "Dashboard",
                description: "Dashboard",
                price: 0.0,
                icon: "fa-tachometer-alt",
                onClick: {
                    type: "navigate",
                    params: `/client/${params.clientId}/project/${params.projectId}?${setStream(project.StreamId.toString()).toString()}`,
                },
                disabled: false,
                projectTypeIds: [1, 2, 3, 5, 6, 7, 8, 9],
                children: [],
            },
            {
                name: "Platforms",
                description: "Platforms",
                price: 0.0,
                icon: "fa-globe",
                onClick: {
                    type: "expand",
                },
                disabled: false,
                projectTypeIds: [1, 2, 3, 5, 6, 7, 8, 9],
                children: [
                    {
                        name: "X",
                        description: "X",
                        price: 0.0,
                        onClick: {
                            type: "navigate",
                            params: `/client/${params.clientId}/project/${params.projectId}?${setStream("7").toString()}`,
                        },
                        disabled: !project.StreamId.toString().split(",").includes("7"),
                        projectTypeIds: [1, 2, 3, 5, 6, 7, 8, 9],
                        children: [],
                    },
                    {
                        name: "Facebook",
                        description: "Facebook",
                        price: 0.0,
                        onClick: {
                            type: "navigate",
                            params: `/client/${params.clientId}/project/${params.projectId}?${setStream("8").toString()}`,
                        },
                        disabled: !project.StreamId.toString().split(",").includes("8"),
                        projectTypeIds: [1, 2, 3, 5, 6, 7, 8, 9],
                        children: [],
                    },
                    {
                        name: "Instagram",
                        description: "Instagram",
                        price: 0.0,
                        onClick: {
                            type: "navigate",
                            params: `/client/${params.clientId}/project/${params.projectId}?${setStream("9").toString()}`,
                        },
                        disabled: !project.StreamId.toString().split(",").includes("9"),
                        projectTypeIds: [1, 2, 3, 5, 6, 7, 8, 9],
                        children: [],
                    },
                    {
                        name: "YouTube",
                        description: "YouTube",
                        price: 0.0,
                        onClick: {
                            type: "navigate",
                            params: `/client/${params.clientId}/project/${params.projectId}?${setStream("10").toString()}`,
                        },
                        disabled: !project.StreamId.toString().split(",").includes("10"),
                        projectTypeIds: [1, 2, 3, 5, 6, 7, 8, 9],
                        children: [],
                    },
                    {
                        name: "LinkedIn",
                        description: "LinkedIn",
                        price: 0.0,
                        onClick: {
                            type: "navigate",
                            params: `/client/${params.clientId}/project/${params.projectId}?${setStream("11").toString()}`,
                        },
                        disabled: !project.StreamId.toString().split(",").includes("11"),
                        projectTypeIds: [2, 3, 8, 9],
                        children: [],
                    },
                ],
            },
            {
                name: "per Contact Notification",
                description: "Whatever is new, the system will find you... if you tell it where you are",
                price: 0.0,
                icon: "fa-bell",
                onClick: {
                    type: "callback",
                    params: "setNotificationManagementModalOpen(true)",
                },
                disabled: checkDisabled([
                    {
                        projectTypeId: 5,
                        productCodes: ["KPAN00", "KPBN00", "KPRN00", "KPEN00", "KPON00", "KPEN00"],
                    },
                    {
                        projectTypeId: 8,
                        productCodes: ["CPAN00", "CPBN00", "CPAN00"],
                    },
                    {
                        projectTypeId: 9,
                        productCodes: ["CIAN00", "CIBN00"],
                    },
                    {
                        projectTypeId: 5,
                        productCodes: ["KPBN00", "KPRN00", "KPEN00"],
                    },
                    {
                        projectTypeId: 8,
                        productCodes: ["CPAN00", "CPBN00", "CPAN00"],
                    },
                    {
                        projectTypeId: 9,
                        productCodes: ["CIAN00", "CIBN00"],
                    },
                ]),
                projectTypeIds: [1, 2, 3, 5, 7, 8, 9],
                children: [],
            },
            {
                name: "Top Authors",
                description: "Description",
                price: 0.0,
                icon: "fa-users",
                onClick: {
                    type: "navigate",
                    params: `/client/${params.clientId}/project/${params.projectId}/top-authors?${setStream(project.StreamId.toString()).toString()}`,
                },
                disabled: false,
                projectTypeIds: [1, 5],
                children: [],
            },
            {
                name: "Competitor Tracking",
                description: "",
                price: 0.0,
                icon: "fa-users-crown",
                onClick: {
                    type: "navigate",
                    params: `/client/${params.clientId}/project/${params.projectId}/competitor-tracking?${setStream(project.StreamId.toString()).toString()}`,
                },
                disabled: false,
                projectTypeIds: [1, 5],
                children: [],
            },
            {
                name: "Post Listing",
                description: "Description",
                price: 0.0,
                icon: "fa-list",
                onClick: {
                    type: "none",
                },
                disabled: false,
                projectTypeIds: [1, 2, 3, 5, 6, 7, 8, 9],
                children: [],
            },
            {
                name: "Sentiment Rating",
                description: "Machine trained tech which accuratly does the heavy lifting... in micro seconds",
                price: 490.0,
                icon: "fa-star-half-alt",
                onClick: {
                    type: "navigate",
                    params: `/client/${params.clientId}/project/${params.projectId}/sentiment?${query.toString()}`,
                },
                disabled: checkDisabled([
                    {
                        projectTypeId: 5,
                        productCodes: ["KPSZ10"],
                    },
                    {
                        projectTypeId: 5,
                        productCodes: ["KPSZ10"],
                    },
                ]),
                projectTypeIds: [1, 5, 6, 7],
                children: [],
            },
            {
                name: "Ave calc's",
                description: "Ave calculations",
                price: 0.0,
                icon: "fa-square-root-alt",
                onClick: {
                    type: "none",
                },
                disabled: false,
                projectTypeIds: [1, 5, 6, 7],
                children: [],
            },
            {
                name: "Reaction Lists",
                description: `
                    <p>List the words that really could affect you and your brand</p>
                    <p>Need more than 10 words? Add an extra list or 2</p>
                `,
                price: 135.0,
                icon: "fa-comment-alt-exclamation",
                onClick: {
                    type: "navigate",
                    params: `/client/${params.clientId}/project/${params.projectId}/reaction-lists?${query.toString()}`,
                },
                children: [],
                disabled: checkDisabled([
                    {
                        projectTypeId: 5,
                        productCodes: ["KCWB01", "KPRW10"],
                    },
                    {
                        projectTypeId: 5,
                        productCodes: ["KPRW10"],
                    },
                ]),
                projectTypeIds: [1, 5],
            },
            {
                name: "Response option Alert",
                description: "Response option Alert",
                price: 0.0,
                icon: "fa-comments",
                onClick: {
                    type: "none",
                },
                disabled: false,
                projectTypeIds: [1, 2, 3, 5, 6, 7, 8, 9],
                children: [],
            },
            {
                name: "Influencer Monitoring",
                description: "Influencer Monitoring",
                price: 0.0,
                icon: "fa-users",
                onClick: {
                    type: "navigate",
                    params: `/client/${params.clientId}/project/${params.projectId}/influencer-comparison?${query.toString()}`,
                },
                disabled: false,
                projectTypeIds: [1, 5],
                children: [],
            },
            {
                name: "Deep Dive",
                description: "Deep Dive",
                price: 530.0,
                icon: "fa-chart-bar",
                onClick: {
                    type: "navigate",
                    params: `/client/${params.clientId}/project/${params.projectId}/deep-dive?${query.toString()}`,
                },
                disabled: checkDisabled([
                    {
                        projectTypeId: 1,
                        productCodes: ["KPVT10"],
                    },
                    {
                        projectTypeId: 5,
                        productCodes: ["KPVT10"],
                    },
                ]),
                projectTypeIds: [1, 5],
                children: [],
            },
            {
                name: "Follower Growth",
                description: "Follower Growth",
                price: 0.0,
                icon: "fa-users",
                onClick: {
                    type: "navigate",
                    params: `/client/${params.clientId}/project/${params.projectId}/performance-measurement?${query.toString()}`,
                },
                disabled: false,
                projectTypeIds: [1, 2, 3, 5, 6, 7, 8, 9],
                children: [],
            },
            {
                name: "Sort Bins",
                description: "A sub-database to sort and store clips",
                price: 330.0,
                icon: "fa-folders",
                onClick: {
                    type: "none",
                    // type: "navigate",
                    // params: `/client/${params.clientId}/project/${params.projectId}/sort-bin`
                },
                disabled: checkDisabled([
                    {
                        projectTypeId: 1,
                        productCodes: ["KPOZ10"],
                    },
                    {
                        projectTypeId: 2,
                        productCodes: ["CPOZ10"],
                    },
                    {
                        projectTypeId: 3,
                        productCodes: ["CIOZ10"],
                    },
                ]),
                projectTypeIds: [1, 2, 3, 5, 6, 7, 8, 9],
                children: [],
            },
            {
                name: "Input Bins",
                description: "With more complex projects and design puzzles... we need extra input data",
                price: 180.0,
                icon: "fa-arrow-circle-down",
                onClick: {
                    type: "none",
                    // type: "navigate",
                    // params: `/client/${params.clientId}/project/${params.projectId}/sort-bin`
                },
                disabled: checkDisabled([
                    {
                        projectTypeId: 1,
                        productCodes: ["KBXT10", "KPXF10", "KPXI10", "KPXY10"],
                    },
                    {
                        projectTypeId: 5,
                        productCodes: ["KBXT10", "KPXF10", "KPXI10", "KPXY10"],
                    },
                    {
                        projectTypeId: 6,
                        productCodes: ["KTXT10"],
                    },
                    {
                        projectTypeId: 7,
                        productCodes: ["KBXT10"],
                    },
                ]),
                projectTypeIds: [1, 5, 6, 7],
                children: [],
            },
            {
                name: "Tree View",
                description: "Tree View",
                price: 0.0,
                icon: "fa-folder-tree",
                onClick: {
                    type: "navigate",
                    params: `/client/${params.clientId}/project/${params.projectId}/treeview?${query.toString()}`,
                },
                disabled: false,
                projectTypeIds: [1, 2, 3, 5, 6, 7, 8, 9],
                children: [],
            },
            {
                name: "Stats Analysis",
                description: "Single period graphs and charts",
                price: 0.0,
                icon: "fa-chart-bar",
                onClick: {
                    type: "callback",
                    params: "setReportModalOpen(true)",
                },
                disabled: false,
                projectTypeIds: [1, 2, 3, 5, 6, 7, 8, 9],
                children: [],
            },
            {
                name: "Trend Analysis",
                description: "Multiple period data displayed as line graphs",
                price: 0.0,
                icon: "fa-file-chart-line",
                onClick: {
                    type: "callback",
                    params: "setReportModalOpen(true)",
                },
                disabled: false,
                projectTypeIds: [1, 2, 3, 5, 6, 7, 8, 9],
                children: [],
            },
            {
                name: "Narrative Analysis",
                description: "Trained business analysts put data into words... and also look for things...",
                price: 0.0,
                icon: "fa-comment-alt-lines",
                onClick: {
                    type: "callback",
                    params: "setFpQueryModalOpen(true)",
                },
                disabled: false,
                projectTypeIds: [1, 2, 3, 5, 6, 7, 8, 9],
                children: [],
            },
        ];

        if (project) {
            setState(data.filter((x) => x.projectTypeIds.includes(project.ProjectTypeId)));
        }
    }, [params.clientId, params.projectId, checkDisabled, project, query, setStream]);

    return state;
};
